import ImageNoticeCardPreview from "./ImageNoticeCardPreview";
import { resolveImageNoticeHeroMedia } from "../../utils/imageNoticeHero";
import NoticeCardPreview from "./NoticeCardPreview";
import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { audienceRefusalMessage } from "../../domain/notices/audienceRefusal";
import { useNoticeForm } from "../../hooks/useNoticeForm";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { api } from "../../services/api";
import { lightTheme } from "../../styles/theme";
import { useQueryClient } from "@tanstack/react-query";
import { DASHBOARD_QUERY_KEY } from "../../hooks/useDashboardData";
import { AnimatePresence } from "framer-motion";
import {
  Container,
  Heading,
  FormContent,
  FixedContainer,
  FixedHeader,
  FixedContent,
  FixedFooter,
} from "./FormComponents";
import {
  ProgressContainer,
  ProgressStep,
  StepNumber,
  StepLabel,
  ProgressLine,
  NavigationSection,
  NavigationButtons,
} from "./ProgressComponents";
import {
  TitleStep,
  ContentStep,
  DetailsStep,
  ReviewStep,
  AttachmentStep,
  EventInfoStep,
  ImageUploadStep,
  ImageTitleStep,
  ImageOnlyTitleStep,
  CustomizationsStep,
} from "./StepComponents";
import EventSettingsStep from "./EventSettingsStep";
import EventPublishStep from "./EventPublishStep";
import SubcircleSelectionStep from "./StepComponents/SubcircleSelectionStep";
import PollStep from "./StepComponents/PollStep";
import FillInTheBlanksStep, {
  FILL_IN_THE_BLANKS_FORM_ID,
} from "./FillInTheBlanksStep";
import NoticeFeedbackModal from "./NoticeFeedbackModal";
import {
  Dialog,
  useTheme,
  useMediaQuery,
  IconButton,
  Box,
  Button,
  Typography,
  DialogActions,
  Avatar,
  Paper,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { formText } from "./FormText";
import { NoticeType } from "../../types/notice";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../shared";
import { buildNoticePayload } from "../../domain/notices/noticePayload";
import type {
  FillInTheBlanksStart,
  WizardStepName,
} from "../../domain/notices/wizardSession";
import {
  fillTemplateNotice,
  isHandEdited,
  type TemplateNoticeText,
  type TemplateBlankValues,
} from "../../domain/notices/templateBlanks";
import { FILL_IN_THE_BLANKS_STEP } from "../../config/noticeFlows";
import { noticeTypeSupportsScheduledPublish } from "../../utils/noticeScheduling";
import MobileAttachmentComposer from "./MobileAttachmentComposer";
import MobileEventComposer from "./MobileEventComposer";
import MobileInfoNoticeComposer from "./MobileInfoNoticeComposer";
import MobilePollComposer from "./MobilePollComposer";
import { composerModalScopeSx } from "./composerBorderStyles";
import { parseSubcircleName } from "../../utils/subcircleUtils";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { useUsers } from "../../hooks/useUsers";
import type { EventRecurrenceFrequency } from "../../domain/events/eventRecurrence";

interface CreationWizardProps {
  noticeType?: NoticeType;
  /** Wizard flow id (e.g. claims uses poll API with claim mode). */
  flowId?: string;
  onClose?: () => void;
  open?: boolean;
  /** Shown under the main title (e.g. direct-notice recipient hint). */
  wizardSubtitle?: string;
  initialEventDate?: Date | null;
  initialFormData?: {
    title?: string;
    body?: string;
    eventBackgroundImage?: string;
    eventFontColor?: string;
    audience?: string;
    targetSubcircle?: string;
    /** Internal team only (Events) */
    staffOnly?: boolean;
    isUrgent?: boolean;
    templateId?: string;
    draftId?: string; // ID of the draft to delete when sending
    targetUserId?: string;
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    eventReminderModes?: string[];
    rsvpEnabled?: boolean;
    eventRecurrenceFrequency?: EventRecurrenceFrequency;
    eventRecurrenceUntil?: Date | null;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    pollOptions?: Array<{ optionId: string; label: string; order: number }>;
    pollClosesAt?: string;
    pollMode?: "standard" | "claim";
  };
  /** A Template's Blanks to fill in first (desktop only). */
  fillInTheBlanks?: FillInTheBlanksStart;
  /** The step to open on (desktop only); step 1 when not set. */
  startAt?: WizardStepName;
  onDataRefresh?: () => void;
  onSelectDirectUser?: (user: {
    id: string;
    name: string;
    email?: string;
    phoneNumber?: string;
  }) => void;
}

const CreationWizard: React.FC<CreationWizardProps> = ({
  noticeType,
  flowId,
  onClose,
  open = true,
  wizardSubtitle,
  initialEventDate,
  initialFormData,
  fillInTheBlanks,
  startAt,
  onDataRefresh,
  onSelectDirectUser,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const { data: users = [] } = useUsers();
  const { subcircles } = useAccessibleCircles();
  const queryClient = useQueryClient();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [isProcessingNext, setIsProcessingNext] = useState(false);
  const [showDraftSaveModal, setShowDraftSaveModal] = useState(false);
  const [embeddedDirectUser] = useState<{
    id: string;
    name: string;
    email?: string;
    phoneNumber?: string;
  } | null>(null);
  const effectiveNoticeType: NoticeType | undefined = embeddedDirectUser
    ? "direct"
    : noticeType;
  const effectiveInitialFormData = useMemo(() => {
    if (!embeddedDirectUser) return initialFormData;
    return {
      ...(initialFormData || {}),
      targetUserId: embeddedDirectUser.id,
    };
  }, [embeddedDirectUser, initialFormData]);

  // Decided fresh for every handover, so resizing never adds or removes a
  // step mid-session. Phones and tablets keep click-to-fill Blanks in their
  // composers.
  const isMdDownOnOpen = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
  });
  const [startsWithBlanks, setStartsWithBlanks] = useState(
    () => Boolean(fillInTheBlanks) && !isMdDownOnOpen,
  );
  // Phones and tablets open their single-screen composers filled in instead.
  const [startStep, setStartStep] = useState(() =>
    isMdDownOnOpen ? undefined : startAt,
  );
  // What startsWithBlanks/startStep were last decided for; a change in any of
  // these is a new handover, so the wizard opens on its own terms rather than
  // whatever step or Blanks state an earlier handover left behind.
  const lastHandover = useRef({
    noticeType: effectiveNoticeType,
    flowId,
    initialFormData: effectiveInitialFormData,
  });
  const isNewHandover =
    lastHandover.current.noticeType !== effectiveNoticeType ||
    lastHandover.current.flowId !== flowId ||
    lastHandover.current.initialFormData !== effectiveInitialFormData;
  if (isNewHandover) {
    lastHandover.current = {
      noticeType: effectiveNoticeType,
      flowId,
      initialFormData: effectiveInitialFormData,
    };
    const nextStartsWithBlanks = Boolean(fillInTheBlanks) && !isMdDownOnOpen;
    if (nextStartsWithBlanks !== startsWithBlanks) {
      setStartsWithBlanks(nextStartsWithBlanks);
    }
    const nextStartStep = isMdDownOnOpen ? undefined : startAt;
    if (nextStartStep !== startStep) {
      setStartStep(nextStartStep);
    }
  }

  useBodyScrollLock(Boolean(open) || showDraftSaveModal);

  const {
    currentStep,
    formData,
    errors,
    showModal,
    modalMessage,
    isSuccess,
    isSubmitting,
    currentFlow,
    applyFormPatch,
    handleChange,
    handleFileUpload,
    handleFileRemove,
    handleAddAttachmentFromUrl,
    handleSubmit,
    handleNext,
    handlePrevious,
    handleModalClose,
    showConfirmation,
    recipientCount,
    isUploading,
    uploadStatusMessage,
    uploadProgress,
    cancelUpload,
    handleConfirmSubmit,
    handleCancelSubmit,
  } = useNoticeForm(
    effectiveNoticeType,
    onClose,
    onDataRefresh,
    effectiveInitialFormData,
    flowId,
    startsWithBlanks,
    startStep,
  );

  const [blankValues, setBlankValues] = useState<TemplateBlankValues>({});
  if (isNewHandover && Object.keys(blankValues).length > 0) {
    setBlankValues({});
  }

  // The form keeps the filled-in text as you type, so a Draft saved from
  // Fill in the values keeps the values too.
  const handleBlankValuesChange = useCallback(
    (values: TemplateBlankValues) => {
      setBlankValues(values);
      if (fillInTheBlanks) {
        applyFormPatch(fillTemplateNotice(fillInTheBlanks, values));
      }
    },
    [applyFormPatch, fillInTheBlanks],
  );

  const handleBlanksContinue = useCallback(
    (filled: TemplateNoticeText) => {
      applyFormPatch(filled);
      handleNext();
    },
    [applyFormPatch, handleNext],
  );

  // Back to Fill in the values rebuilds the text from the Template, so hand
  // edits made since Continue are confirmed before they're lost.
  const [showBackToBlanksWarning, setShowBackToBlanksWarning] = useState(false);

  const goBackToBlanks = useCallback(() => {
    setShowBackToBlanksWarning(false);
    if (fillInTheBlanks) {
      applyFormPatch(fillTemplateNotice(fillInTheBlanks, blankValues));
    }
    handlePrevious();
  }, [applyFormPatch, blankValues, fillInTheBlanks, handlePrevious]);

  const handleBack = () => {
    const backLeadsToBlanks =
      currentFlow.steps[currentStep - 2]?.component ===
      FILL_IN_THE_BLANKS_STEP.component;
    if (!fillInTheBlanks || !backLeadsToBlanks) {
      handlePrevious();
    } else if (
      isHandEdited(fillInTheBlanks, blankValues, {
        title: formData.title ?? "",
        body: formData.body ?? "",
      })
    ) {
      setShowBackToBlanksWarning(true);
    } else {
      goBackToBlanks();
    }
  };

  // Debounced handleNext for mobile to prevent multiple rapid clicks
  const handleNextDebounced = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isProcessingNext) {
        return;
      }

      setIsProcessingNext(true);
      handleNext();

      // Reset after a short delay to allow state updates
      setTimeout(() => {
        setIsProcessingNext(false);
      }, 300);
    },
    [handleNext, isProcessingNext]
  );

  // Set initial event date and time when provided. A day picked on a calendar
  // starts at 09:00; a day handed over with the rest of the Event never gets a
  // time it wasn't given.
  const initialEventStartTime = initialFormData?.eventStartTime?.trim();
  const dayHandedOver = Boolean(initialFormData?.eventStartDate);
  useEffect(() => {
    if (initialEventDate && noticeType === "custom_event") {
      handleChange({
        target: { name: "eventStartDate", value: initialEventDate },
      });
      if (initialEventStartTime || !dayHandedOver) {
        handleChange({
          target: {
            name: "eventStartTime",
            value: initialEventStartTime || "09:00",
          },
        });
      }
    }
  }, [initialEventDate, initialEventStartTime, dayHandedOver, noticeType, handleChange]);

  // Focus the close button when dialog opens to prevent aria-hidden warning
  useEffect(() => {
    if (open && closeButtonRef.current) {
      // Small delay to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Check if form has any content
  const hasFormContent = useCallback(() => {
    return !!(
      (formData.title && formData.title.trim()) ||
      (formData.body && formData.body.trim()) ||
      (formData.attachmentUrl && formData.attachmentUrl.trim()) ||
      (formData.uploadedFiles && formData.uploadedFiles.length > 0) ||
      formData.eventStartDate ||
      (formData.eventStartTime && formData.eventStartTime.trim()) ||
      (formData.eventLocation &&
        formData.eventLocation.trim() &&
        formData.eventLocation.trim() !== user?.schoolName?.trim()) ||
      (formData.eventBackgroundImage && formData.eventBackgroundImage.trim()) ||
      (formData.targetSubcircle && formData.targetSubcircle.trim())
    );
  }, [formData, user?.schoolName]);

  const publishAudienceName = useMemo(() => {
    if (formData.audience === "subcircle" && formData.targetSubcircle) {
      if (formData.targetSubcircle === user?.schoolId) {
        return user?.schoolName || "Main Circle";
      }

      const matchedSubcircle = subcircles.find(
        (subcircle) => subcircle.circleId === formData.targetSubcircle
      );
      if (matchedSubcircle?.name) {
        return matchedSubcircle.name;
      }

      return parseSubcircleName(formData.targetSubcircle);
    }

    if (formData.audience === "circle") {
      return user?.schoolName || "Entire Circle";
    }

    return "";
  }, [
    formData.audience,
    formData.targetSubcircle,
    subcircles,
    user?.schoolId,
    user?.schoolName,
  ]);
  const audienceWizardSubtitle = useMemo(() => {
    if (!publishAudienceName) return "";
    if (formData.audience !== "circle" && formData.audience !== "subcircle") return "";
    return `All members of ${publishAudienceName} will see this notice in the app.`;
  }, [formData.audience, publishAudienceName]);
  const effectiveWizardSubtitle =
    (embeddedDirectUser
      ? `Only ${embeddedDirectUser.name} will see this notice in the app.`
      : wizardSubtitle) || audienceWizardSubtitle;
  // Save draft to backend
  /** Saves the Draft; false (after telling the person why) when it couldn't be saved. */
  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (!token || !user?.schoolId) return false;

    try {
      const noticeData = buildNoticePayload(
        formData,
        { templateId: initialFormData?.templateId },
        "draft",
      );

      // Save the new copy before removing the old one, so a refused save
      // doesn't lose the Draft being edited.
      await api.notices.createDraft(noticeData, token);

      if (initialFormData?.draftId) {
        try {
          await api.notices.delete(initialFormData.draftId, user.schoolId, token);
        } catch (error) {
          console.error("Error deleting old draft:", error);
        }
      }

      // Invalidate notices query to refresh the draft list
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      showMessage({
        message:
          audienceRefusalMessage(error) ??
          "Couldn't save your draft. Check your connection and try again.",
        severity: "error",
      });
      return false;
    }
  }, [formData, initialFormData, token, user, queryClient, showMessage]);

  // Handle close with draft check
  const handleCloseWithDraftCheck = useCallback(() => {
    // If editing an existing draft, show modal to keep or delete
    if (initialFormData?.draftId) {
      setShowDraftSaveModal(true);
      return;
    }
    
    // For new drafts, show the save modal if there's content
    if (hasFormContent()) {
      setShowDraftSaveModal(true);
    } else {
      onClose?.();
    }
  }, [hasFormContent, onClose, initialFormData?.draftId]);

  // Handle save draft and close
  // Stays open if the save is refused, so nothing typed is lost.
  const handleSaveDraftAndClose = useCallback(async () => {
    setShowDraftSaveModal(false);
    if (await saveDraft()) onClose?.();
  }, [saveDraft, onClose]);

  // Handle save draft from confirmation modal (cancel send, save draft, close)
  const handleSaveDraftFromConfirmation = useCallback(async () => {
    handleCancelSubmit();
    if (await saveDraft()) onClose?.();
  }, [handleCancelSubmit, saveDraft, onClose]);

  // Handle discard and close
  const handleDiscardAndClose = useCallback(() => {
    localStorage.removeItem("noticeDraft");
    setShowDraftSaveModal(false);
    onClose?.();
  }, [onClose]);

  // Handle delete draft and close
  const handleDeleteDraftAndClose = useCallback(async () => {
    if (initialFormData?.draftId && user?.schoolId && token) {
      try {
        await api.notices.delete(initialFormData.draftId, user.schoolId, token);
        queryClient.invalidateQueries({ queryKey: ["notices"] });
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      } catch (error) {
        console.error("Error deleting draft:", error);
      }
    }
    setShowDraftSaveModal(false);
    onClose?.();
  }, [initialFormData?.draftId, user?.schoolId, token, queryClient, onClose]);

  // Memoized: formData (and therefore this component) re-renders on every
  // keystroke while composing, and users can be a large org-wide list.
  const directRecipient = useMemo(
    () =>
      initialFormData?.targetUserId
        ? users.find((item: any) => item?.id === initialFormData.targetUserId) ||
          null
        : null,
    [users, initialFormData?.targetUserId]
  );

  if (user?.userType !== "team" && !user?.isSchoolApproved) {
    return (
      <Container>
        <Heading>Create a Notice</Heading>
      </Container>
    );
  }

  const renderStep = () => {
    const stepProps = {
      formData,
      errors,
      handleChange,
      applyFormPatch,
      handleFileUpload,
      handleFileRemove,
      handleAddAttachmentFromUrl,
      isUploading,
      uploadStatusMessage,
      uploadProgress,
      onCancelUpload: cancelUpload,
      noticeFlowId: currentFlow.id,
    };

    const currentStepConfig = currentFlow.steps[currentStep - 1];

    if (
      currentStepConfig.component === FILL_IN_THE_BLANKS_STEP.component &&
      fillInTheBlanks
    ) {
      return (
        <FillInTheBlanksStep
          start={fillInTheBlanks}
          values={blankValues}
          onValuesChange={handleBlankValuesChange}
          onContinue={handleBlanksContinue}
          isEvent={currentFlow.id === "custom_event"}
          artworkUrl={currentFlow.id === "image_notice" ? formData.uploadedFiles?.find(file => file.contentType?.startsWith("image/"))?.fileUrl : undefined}
          backgroundImage={
            formData.eventBackgroundImage ||
            formData.uploadedFiles?.find((file) =>
              file.contentType?.startsWith("image/"),
            )?.fileUrl
          }
        />
      );
    }

    const StepComponent = {
      TitleStep,
      ContentStep,
      DetailsStep,
      ReviewStep,
      AttachmentStep,
      EventInfoStep,
      EventSettingsStep,
      EventPublishStep,
      ImageUploadStep,
      ImageTitleStep,
      ImageOnlyTitleStep,
      CustomizationsStep,
      SubcircleSelectionStep,
      PollStep,
    }[currentStepConfig.component];

    return StepComponent ? <StepComponent {...stepProps} /> : null;
  };

  /** Attachment + image notices: single-screen composer on phones and tablets. */
  const isMobileAttachmentComposer =
    isMdDown &&
    (currentFlow.id === "image" || currentFlow.id === "image_notice");
  /** Info notices: single-screen composer on phones and tablets. */
  const isMobileInfoComposer =
    isMdDown && currentFlow.id === "info";
  /** Poll / claims notices: single-screen composer on phones and tablets. */
  const isMobilePollComposer =
    isMdDown &&
    (currentFlow.id === "poll" || currentFlow.id === "claims");
  const isMobileEventComposer = false;
  const isMobileSingleScreenComposer =
    isMobileAttachmentComposer ||
    isMobileInfoComposer ||
    isMobilePollComposer;
  /** Full-bleed composers run their own entrance; skip Container fade to avoid double opacity. */
  const isMobileFullBleedComposer =
    isMobileSingleScreenComposer || isMobileEventComposer;

  /** Wider-than-sm layouts still need full-screen framing for single-screen composers. */
  const dialogMobileLayout = isMobile || isMobileSingleScreenComposer;
  const isMobileDirectCompose =
    isMobile && currentFlow.id === "direct" && !!initialFormData?.targetUserId;
  const directRecipientName =
    directRecipient?.name ||
    wizardSubtitle?.match(/Only (.+) will see this notice/i)?.[1] ||
    "Recipient";

  const isReviewStep = currentStep === currentFlow.steps.length;
  const currentStepComponent =
    currentFlow.steps[currentStep - 1]?.component ?? "";
  const isFillInTheBlanksStep =
    currentStepComponent === FILL_IN_THE_BLANKS_STEP.component;
  const isCompactContentStep =
    currentStepComponent === "EventSettingsStep" ||
    currentStepComponent === "EventInfoStep" ||
    currentStepComponent === "EventPublishStep" ||
    currentStepComponent === "PollStep";
  const isSchedulePublish =
    formData.publishMode === "schedule" &&
    noticeTypeSupportsScheduledPublish(formData.type);

  const modalContent = (
    <Container
      initial={isMobileFullBleedComposer ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={
        isMobileFullBleedComposer ? { duration: 0 } : { duration: 0.5 }
      }
    >
      {onClose && !isMobileEventComposer && (
        <IconButton
          ref={closeButtonRef}
          onClick={handleCloseWithDraftCheck}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: {
              xs: isMobileSingleScreenComposer
                ? "calc(env(safe-area-inset-top, 0px) + 6px)"
                : isMobileDirectCompose
                ? "calc(env(safe-area-inset-top, 0px) + 10px)"
                : "calc(env(safe-area-inset-top, 0px) + 64px)",
              sm: isMobileSingleScreenComposer
                ? "calc(env(safe-area-inset-top, 0px) + 6px)"
                : 16,
              md: 16,
            },
            right: {
              xs: isMobileSingleScreenComposer ? 10 : 16,
              sm: isMobileSingleScreenComposer ? 10 : 16,
              md: 16,
            },
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: theme.palette.whiteOverlays.overlay90,
            border: "none",
            borderRadius: "50%",
            padding: 1,
            minWidth: 32,
            minHeight: 32,
            boxShadow: `0 2px 8px ${lightTheme.colors.shadowBlack15}`,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: theme.palette.grey[100],
              transform: "scale(1.1)",
            },
          }}
        >
          <CloseIcon sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
        </IconButton>
      )}
      {isMobileAttachmentComposer ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <MobileAttachmentComposer
            mode={currentFlow.id === "image_notice" ? "image" : "attachment"}
            formData={formData}
            errors={errors}
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={cancelUpload}
            isSubmitting={isSubmitting}
            handleChange={handleChange}
            applyFormPatch={applyFormPatch}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
            handleSubmit={handleSubmit}
          />
        </Box>
      ) : isMobileInfoComposer ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <MobileInfoNoticeComposer
            formData={formData}
            errors={errors}
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={cancelUpload}
            isSubmitting={isSubmitting}
            handleChange={handleChange}
            applyFormPatch={applyFormPatch}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
            handleSubmit={handleSubmit}
          />
        </Box>
      ) : isMobilePollComposer ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <MobilePollComposer
            formData={formData}
            errors={errors}
            isSubmitting={isSubmitting}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            noticeFlowId={currentFlow.id}
          />
        </Box>
      ) : isMobileEventComposer ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <MobileEventComposer
            formData={formData}
            errors={errors}
            isSubmitting={isSubmitting}
            isUploading={isUploading}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            onClose={handleCloseWithDraftCheck}
            closeButtonRef={closeButtonRef}
          />
        </Box>
      ) : (
      <FixedContainer data-review-step={isReviewStep ? "true" : undefined}>
        <FixedHeader
          style={
            isMobileDirectCompose
              ? { paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)" }
              : undefined
          }
        >
          <Heading
            initial={{ opacity: 0, y: -30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 15,
              mass: 1,
            }}
            style={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              marginTop: isMobileDirectCompose ? "0rem" : "1.2rem",
              marginBottom: isMobileDirectCompose ? "0.45rem" : "1.2rem",
              letterSpacing: "-0.2px",
              color: theme.palette.primary.main,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            {currentFlow.name}
          </Heading>
          {effectiveWizardSubtitle && (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mt: -0.5,
                mb: 2.5,
                px: 2,
                maxWidth: 520,
                mx: "auto",
              }}
            >
              {effectiveWizardSubtitle}
            </Typography>
          )}
          {isMobileDirectCompose && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                mx: "auto",
                maxWidth: 520,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.04)
                    : theme.palette.grey[50],
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Avatar
                alt={directRecipientName}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: getAvatarColor(directRecipientName),
                  color: "common.white",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {getInitials(directRecipientName)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    lineHeight: 1.2,
                  }}
                >
                  Sending to
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: "1.05rem",
                    lineHeight: 1.35,
                    mt: 0.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {directRecipientName}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ mt: 0.35 }}
                >
                  <LockOutlinedIcon
                    sx={{ fontSize: 14, color: "text.secondary", opacity: 0.85 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.25,
                      fontSize: "0.75rem",
                    }}
                  >
                    Only this person will see this notice
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          )}

          <ProgressContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ProgressLine
              $isActive={currentStep > 1}
              $progress={
                ((currentStep - 1) / (currentFlow.steps.length - 1)) * 100
              }
            />
            {currentFlow.steps.map((step, index) => (
              <ProgressStep
                key={step.id}
                $isActive={currentStep >= index + 1}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <StepNumber $isActive={currentStep >= index + 1}>
                  {currentStep > index + 1 ? <CheckIcon /> : index + 1}
                </StepNumber>
                <StepLabel $isActive={currentStep >= index + 1}>
                  {formText.progressSteps[
                    step.id as keyof typeof formText.progressSteps
                  ] || step.id}
                </StepLabel>
              </ProgressStep>
            ))}
          </ProgressContainer>
        </FixedHeader>

        <FixedContent
          $isReview={isReviewStep}
          $compact={isCompactContentStep}
        >
          <AnimatePresence mode="wait">
            <FormContent
              $wide={isReviewStep}
              $compact={isCompactContentStep}
              key={currentStep}
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <Box sx={[{ width: "100%" }, composerModalScopeSx]}>
                {renderStep()}
              </Box>
            </FormContent>
          </AnimatePresence>
        </FixedContent>

        <FixedFooter $isReview={currentStep === currentFlow.steps.length}>
          <NavigationSection>
            <NavigationButtons>
              {currentStep > 1 &&
                (isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleBack}
                    aria-label="Previous step"
                    sx={{
                      backgroundColor: "common.white",
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`,
                      width: 48,
                      height: 48,
                      "&:hover": {
                        backgroundColor: theme.palette.grey[50],
                      },
                    }}
                  >
                    <ArrowBackIosIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <NoticeboardButton
                    variant="secondary"
                    size="medium"
                    onClick={handleBack}
                  >
                    {formText.buttons.previous}
                  </NoticeboardButton>
                ))}
              {currentStep < currentFlow.steps.length ? (
                isMobile ? (
                  <IconButton
                    ref={nextButtonRef}
                    color="primary"
                    onClick={handleNextDebounced}
                    onTouchStart={(e) => {
                      // Prevent default touch behavior that might interfere
                      e.stopPropagation();
                    }}
                    aria-label="Next step"
                    type="button"
                    disabled={isProcessingNext || isUploading}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "common.white",
                      width: 48,
                      height: 48,
                      marginBottom: 0,
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      "&:hover": {
                        backgroundColor: lightTheme.colors.primaryAccent,
                      },
                      "&:active": {
                        backgroundColor: lightTheme.colors.primaryAccent,
                        transform: "scale(0.95)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                        cursor: "not-allowed",
                      },
                    }}
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <NoticeboardButton
                    variant="primary"
                    size="medium"
                    // Fill in the values hands back its filled text on submit.
                    {...(isFillInTheBlanksStep
                      ? { type: "submit", form: FILL_IN_THE_BLANKS_FORM_ID }
                      : { onClick: handleNext })}
                    disabled={isUploading}
                  >
                    {(() => {
                      const currentStepConfig =
                        currentFlow.steps[currentStep - 1];
                      const isTitleStep = currentStepConfig?.id === "title";
                      const isSimpleNoticeFlow =
                        currentFlow.id === "info" ||
                        currentFlow.id === "urgent" ||
                        currentFlow.id === "direct";
                      return isTitleStep && isSimpleNoticeFlow
                        ? "Write message"
                        : formText.buttons.next;
                    })()}
                  </NoticeboardButton>
                )
              ) : (
                <NoticeboardButton
                  variant="primary"
                  size="medium"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isUploading}
                  sx={{
                    position: "relative",
                    opacity: isSubmitting || isUploading ? 0.5 : 1,
                    cursor: isSubmitting || isUploading ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    "&:hover:not(:disabled)": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                    },
                    "&:active:not(:disabled)": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {isSchedulePublish ? (
                      <ScheduleIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <SendIcon
                        sx={{
                          fontSize: 18,
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "translateX(4px)",
                          },
                        }}
                      />
                    )}
                    <span>
                      {isUploading
                        ? "Uploading…"
                        : isSchedulePublish
                          ? "Schedule"
                          : formText.buttons.submit}
                    </span>
                  </Box>
                </NoticeboardButton>
              )}
            </NavigationButtons>
          </NavigationSection>
        </FixedFooter>
      </FixedContainer>

      )}
      <NoticeFeedbackModal
        open={showModal || isSubmitting || showConfirmation}
        onClose={handleModalClose}
        message={
          isSubmitting
            ? isSchedulePublish
              ? "Your notice is being scheduled..."
              : "Your notice is being published..."
            : modalMessage
        }
        isSuccess={isSuccess}
        isSubmitting={isSubmitting}
        isConfirmation={showConfirmation}
        preview={isMobile ? (currentFlow.id === "image" || currentFlow.id === "image_notice" ? (() => {
          const hero = resolveImageNoticeHeroMedia(formData.uploadedFiles, formData.attachmentUrl);
          return <ImageNoticeCardPreview title={formData.title} body={formData.body} imageUrl={hero?.url} heroIsVideo={hero?.isVideo} createdAt={new Date()} schoolName={publishAudienceName || "Selected audience"} isUrgent={!!formData.isUrgent} />;
        })() : <NoticeCardPreview
            layout="native"
            title={formData.title}
            body={formData.body}
            isEvent={formData.type === "event" || formData.type === "custom_event"}
            eventStartDate={formData.eventStartDate}
            eventStartTime={formData.eventStartTime}
            eventLocation={formData.eventLocation}
            eventBackgroundImage={formData.eventBackgroundImage}
          />
        ) : undefined}
        recipientCount={recipientCount}
        audienceName={publishAudienceName}
        scheduleMode={isSchedulePublish}
        scheduledAtLabel={
          formData.publishAt
            ? new Date(formData.publishAt).toLocaleString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : undefined
        }
        staffOnly={formData.staffOnly === true}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        onSaveDraft={handleSaveDraftFromConfirmation}
        confirmDisabled={isUploading}
      />
    </Container>
  );

  const dialogPaperSx = {
    borderRadius: dialogMobileLayout ? 0 : 4,
    boxShadow: dialogMobileLayout ? "none" : 24,
    p: 0,
    position: "relative",
    maxWidth: dialogMobileLayout ? "100%" : 920,
    width: dialogMobileLayout ? "100%" : "85%",
    /* Fixed height on desktop so the modal does not resize between steps. */
    height: dialogMobileLayout ? "100%" : "90vh",
    maxHeight: dialogMobileLayout ? "100%" : "90vh",
    ...(dialogMobileLayout ? { minHeight: 0 } : {}),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...(dialogMobileLayout ? { margin: 0 } : {}),
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only allow closing via the X button, not backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCloseWithDraftCheck();
      }}
      disableEscapeKeyDown
      disableScrollLock
      maxWidth="lg"
      fullWidth
      fullScreen={dialogMobileLayout}
      aria-label="Create Notice Modal"
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      sx={{
        "& .MuiDialog-paper": dialogPaperSx,
        "& .MuiBackdrop-root": {
          backgroundColor: theme.palette.common.black + "59",
        },
      }}
    >
      {modalContent}
    </Dialog>
    <ActionModal
      open={showBackToBlanksWarning}
      onClose={() => setShowBackToBlanksWarning(false)}
      title="Go back to Fill in the values?"
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="secondary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={() => setShowBackToBlanksWarning(false)}
          >
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={goBackToBlanks}
          >
            Go back
          </NoticeboardButton>
        </Box>
      }
    >
      <Typography variant="body1" sx={{ color: "text.secondary" }}>
        Going back rebuilds the title and text from the Template and your
        values, so the changes you made here will be lost.
      </Typography>
    </ActionModal>
    {/* Draft Save Confirmation Modal */}
    <Dialog
      open={showDraftSaveModal}
      onClose={() => setShowDraftSaveModal(false)}
      disableScrollLock
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {initialFormData?.draftId
            ? "Keep or Remove Draft?"
            : initialFormData?.targetUserId || initialFormData?.targetSubcircle
            ? "Discard direct notice?"
            : "Save as Draft?"}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          {initialFormData?.draftId
            ? "Would you like to keep this draft or remove it?"
            : initialFormData?.targetUserId || initialFormData?.targetSubcircle
            ? "You have unsaved changes. Discard this message, or go back to continue editing."
            : "You have unsaved changes. Would you like to save this notice as a draft before closing?"}
        </Typography>
        <DialogActions sx={{ px: 0, pb: 0, gap: 1 }}>
          {initialFormData?.draftId ? (
            <>
              <Button
                onClick={handleDeleteDraftAndClose}
                variant="outlined"
                color="error"
                sx={{ textTransform: "none" }}
              >
                Remove Draft
              </Button>
              <Button
                onClick={() => {
                  setShowDraftSaveModal(false);
                  onClose?.();
                }}
                variant="contained"
                sx={{
                  textTransform: "none",
                  bgcolor: theme.palette.primary.main,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Keep Draft
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleDiscardAndClose}
                variant="outlined"
                sx={{ textTransform: "none" }}
              >
                Discard
              </Button>
              {!initialFormData?.targetUserId &&
                !initialFormData?.targetSubcircle && (
                <Button
                  onClick={handleSaveDraftAndClose}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    bgcolor: theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Save Draft
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
    </>
  );
};

export default CreationWizard;
